/**
 * Impact of Friction Chart
 *
 * Horizontal stacked bar chart showing notional revenue and expense impact of friction.
 * Revenue: actual (green) + revenue friction (grey) stacked. Revenue friction = actual * 0.75.
 * Expense: actual - expense friction (red) + expense friction overlay (grey). Expense friction = actual * 0.25.
 */
import React, { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import styles from './ImpactOfFrictionChart.module.css';

const REVENUE_FRICTION_RATIO = 0.75;
const EXPENSE_FRICTION_RATIO = 0.25;

const COLOR_REVENUE = '#52c41a';
const COLOR_EXPENSE = '#ef4444';
const COLOR_FRICTION = '#8c8c8c';
/** Friction segments drawn narrower so they're visually distinct from actual */
const FRICTION_WIDTH_SCALE = 0.35;
/** Expense friction overlay height (shows reduction cutting into the bar) */
const FRICTION_OVERLAY_HEIGHT = 8;

interface ImpactOfFrictionChartProps {
  revenue: number;
  expense: number;
  height?: number;
}

export const ImpactOfFrictionChart: React.FC<ImpactOfFrictionChartProps> = ({
  revenue,
  expense,
  height = 90,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { revenueBarData, expenseBarData, maxValue } = useMemo(() => {
    const revenueFriction = revenue * REVENUE_FRICTION_RATIO;
    const expenseFriction = expense * EXPENSE_FRICTION_RATIO;

    const revenueTotal = revenue + revenueFriction;
    const expenseReduced = expense - expenseFriction;

    return {
      revenueBarData: [
        { value: revenue, color: COLOR_REVENUE, label: 'Actual' },
        { value: revenueFriction, color: COLOR_FRICTION, label: 'Friction' },
      ],
      expenseBarData: [
        { value: expenseReduced, color: COLOR_EXPENSE, label: 'Actual' },
        { value: expenseFriction, color: COLOR_FRICTION, label: 'Friction' },
      ],
      maxValue: Math.max(revenueTotal, expense, 1),
    };
  }, [revenue, expense]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;
    if (revenue <= 0 && expense <= 0) return;

    const container = containerRef.current;
    const svgEl = svgRef.current;

    const animationFrameId = requestAnimationFrame(() => {
      if (!svgEl || !containerRef.current) return;
      const svg = d3.select(svgEl);
      svg.selectAll('*').remove();

      const margin = { top: 8, right: 8, bottom: 28, left: 60 };
      const width = Math.max(container.clientWidth, 120);
      const innerWidth = width - margin.left - margin.right;

      const x = d3.scaleLinear().domain([0, maxValue]).range([0, innerWidth]);

      const barHeight = 20;
      const barGap = 24;
      const revenueBarY = 0;
      const expenseBarY = revenueBarY + barHeight + barGap;

      const g = svg
        .attr('width', width)
        .attr('height', height)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Revenue bar
      g.append('text')
        .attr('x', -6)
        .attr('y', revenueBarY + barHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '11px')
        .style('fill', '#595959')
        .text('Revenue');

      // Revenue bar: actual (green) + friction (grey, narrower)
      let offset = 0;
      revenueBarData.forEach((seg, i) => {
        if (seg.value <= 0) return;
        const isFriction = seg.color === COLOR_FRICTION;
        const segWidth = isFriction
          ? x(seg.value) * FRICTION_WIDTH_SCALE
          : x(seg.value);
        g.append('rect')
          .attr('x', offset)
          .attr('y', revenueBarY)
          .attr('width', segWidth)
          .attr('height', barHeight)
          .attr('fill', seg.color)
          .attr('rx', 2);
        offset += segWidth;
      });

      // Expense bar: full actual (red), then friction overlay (grey, narrow, overlays to show reduction)
      const expenseActual = expenseBarData[0];
      const expenseFriction = expenseBarData[1];
      const expenseRedWidth = expenseActual.value > 0 ? x(expenseActual.value) : 0;
      const expenseFullWidth = expenseRedWidth + (expenseFriction.value > 0 ? x(expenseFriction.value) : 0);

      g.append('text')
        .attr('x', -6)
        .attr('y', expenseBarY + barHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '11px')
        .style('fill', '#595959')
        .text('Expense');

      g.append('rect')
        .attr('x', 0)
        .attr('y', expenseBarY)
        .attr('width', expenseFullWidth)
        .attr('height', barHeight)
        .attr('fill', COLOR_EXPENSE)
        .attr('rx', 2);

      if (expenseFriction.value > 0) {
        const frictionWidth = x(expenseFriction.value) * FRICTION_WIDTH_SCALE;
        const overlayX = expenseFullWidth - frictionWidth;
        const overlayY = expenseBarY + (barHeight - FRICTION_OVERLAY_HEIGHT) / 2;
        g.append('rect')
          .attr('x', overlayX)
          .attr('y', overlayY)
          .attr('width', frictionWidth)
          .attr('height', FRICTION_OVERLAY_HEIGHT)
          .attr('fill', COLOR_FRICTION)
          .attr('rx', 2);
      }
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [revenue, expense, revenueBarData, expenseBarData, maxValue, height]);

  return (
    <div className={styles.chartWrap}>
      <div className={styles.chartLabel}>Impact of friction</div>
      {revenue > 0 || expense > 0 ? (
        <div ref={containerRef} className={styles.chartContainer}>
          <svg ref={svgRef} />
        </div>
      ) : (
        <div className={styles.chartPlaceholder}>
          Enter revenue and expense above to see impact of friction
        </div>
      )}
    </div>
  );
};

export default ImpactOfFrictionChart;
